/**
 * SurahList Drawer Component
 * 
 * A full-width top-to-bottom drawer to select Surahs.
 * Replaces the old dropdown menu for better mobile UX.
 */

import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';
import type { SurahListItem } from '@/types/quran';

interface SurahListDrawerProps {
  open: boolean;
  onClose: () => void;
  surahs: SurahListItem[];
  onSelectSurah: (surahNumber: number) => void;
  currentSurahNumber?: number;
}

export function SurahListDrawer({
  open,
  onClose,
  surahs,
  onSelectSurah,
  currentSurahNumber,
}: SurahListDrawerProps) {
  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent
        side="top"
        className="max-h-[85vh] overflow-y-auto rounded-b-2xl pt-10 px-0"
      >
        <SheetHeader className="px-6 mb-4">
          <SheetTitle className="text-center font-bold text-lg">เลือกซูเราะฮ์</SheetTitle>
        </SheetHeader>

        <div className="grid grid-cols-1 divide-y divide-border/40">
          {surahs.map((surah) => (
            <button
              key={surah.number}
              onClick={() => {
                onSelectSurah(surah.number);
                onClose();
              }}
              className={cn(
                'flex items-center gap-4 px-6 py-4 text-left transition-colors',
                'hover:bg-accent/50 active:bg-accent',
                currentSurahNumber === surah.number &&
                'bg-primary/5 text-primary'
              )}
            >
              {/* Number Badge */}
              <span
                className={cn(
                  'shrink-0 w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold shadow-sm',
                  currentSurahNumber === surah.number
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground'
                )}
              >
                {surah.number}
              </span>

              {/* Name Details */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-base font-semibold truncate">
                    {surah.englishName}
                  </span>
                  <span className="font-amiri-quran text-lg text-primary/80">
                    {surah.name}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <span className="truncate max-w-[120px]">
                    {surah.englishNameTranslation}
                  </span>
                  <span>•</span>
                  <span>{surah.numberOfAyahs} อายะฮ์</span>
                  <span>•</span>
                  <span>
                    {surah.revelationType === 'Meccan' ? 'มักกะฮ์' : 'มะดีนะฮ์'}
                  </span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

export default SurahListDrawer;
